import { AppProps } from "next/app";
import Head from "next/head";
import { Provider } from "react-redux";

import { BCAThemeProvider, SSRProvider, newBcaTheme } from "@bca/ui-library";
import { createGlobalStyle } from "styled-components";

import { ErrorProvider } from "../src/context/ErrorContext";
import { store } from "../src/redux/store";
import ErrorPage from "../src/common/ErrorPage/ErrorPage";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import AppInsightsService from "../src/services/AppInsightsService";
import { AuthProvider } from "@bca-auth0/bca-auth0-provider";

const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    min-height: 100%;
    min-width: 100%;
    font-family: "Roboto", "sans-serif";
    overflow-x: hidden;
  }

  div#__next {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  textarea {
    font-family: "Roboto", "sans-serif";
  }

  ul[role="menubar"] li:hover ul[role="submenu"],
  ul[role="menubar"] li:hover div[role="submenu"] {
    z-index: 2;
  }
`;

interface PageProps {
  user: { firstName: string; lastName: string };
  idToken: string;
  authProviderDomain: string;
  authProviderClientId: string;
  authProviderAudience: string;
  authProviderScope: string;
  redirectUri: string;
  appConfigurationUrl: string;
}

interface VehicleSearchAppPageProps extends AppProps {
  pageProps: PageProps;
  appInsightsConnectionString: string;
}

export default function VehicleSearchApp({
  Component,
  pageProps,
  appInsightsConnectionString,
}: VehicleSearchAppPageProps) {
  const {
    user,
    idToken,
    authProviderDomain,
    authProviderClientId,
    authProviderAudience,
    authProviderScope,
    redirectUri,
  } = pageProps;

  const appInsightsInstance = AppInsightsService.getInstance(
    appInsightsConnectionString
  );
  const reactPlugin = appInsightsInstance.reactPlugin;
  return (
    <SSRProvider>
      <AuthProvider
        domain={authProviderDomain}
        clientId={authProviderClientId}
        useRefreshTokens
        authorizationParams={{
          redirect_uri: redirectUri,
          audience: authProviderAudience,
          scope: authProviderScope,
        }}
      >
        <Head>
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width"
          />
        </Head>
        <GlobalStyle />
        <BCAThemeProvider theme={newBcaTheme}>
          <AppInsightsErrorBoundary
            onError={() => <ErrorPage user={user} idToken={idToken} />}
            appInsights={reactPlugin}
          >
            <Provider store={store}>
              <ErrorProvider>
                <Component {...pageProps} />
              </ErrorProvider>
            </Provider>
          </AppInsightsErrorBoundary>
        </BCAThemeProvider>
      </AuthProvider>
    </SSRProvider>
  );
}

VehicleSearchApp.getInitialProps = async () => {
  return {
    pageProps: {
      auth0Domain: process.env.AUTH0_DOMAIN,
      auth0ClientId: process.env.AUTH0_CLIENT_ID,
      auth0Audience: process.env.AUTH0_AUDIENCE,
      auth0Scope: process.env.AUTH0_SCOPE,
      redirectUri: process.env.AUTH_PROVIDER_REDIRECT_URI,
      appConfigurationUrl: process.env.APP_CONFIGURATION_URL,
    },
    appInsightsConnectionString:
      process.env.APPLICATIONINSIGHTS_CONNECTION_STRING,
  };
};
